import { useState, useEffect, useCallback } from 'react';

/**
 * localStorage key for persisting generation jobs across navigation/refresh.
 */
const STORAGE_KEY = 'scoutiq.generationJobs';

/**
 * Read jobs from localStorage (safe).
 */
function readJobs() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

/**
 * Write jobs to localStorage (safe).
 */
function writeJobs(jobs) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(jobs));
  } catch { /* quota exceeded — ignore */ }
}

/**
 * Purge completed/failed jobs older than 24 hours on read.
 */
const MAX_PROCESSING_AGE_MS = 30 * 60 * 1000; // 30 min — auto-expire stuck jobs

function pruneOldJobs(jobs) {
  const now = Date.now();
  const cutoff = now - 24 * 60 * 60 * 1000;
  return jobs.filter(j => {
    if (j.status === 'processing') {
      const age = now - new Date(j.startedAt).getTime();
      return age < MAX_PROCESSING_AGE_MS;
    }
    const ts = j.completedAt || j.startedAt;
    return ts && new Date(ts).getTime() > cutoff;
  });
}

// On page load, remove 'processing' jobs — SSE connections are lost on refresh
// so these jobs can never complete client-side. (Runs once per page load; SPA
// navigation re-uses the already-loaded module.)
(function cleanupStaleOnLoad() {
  const jobs = readJobs();
  const cleaned = jobs.filter(j => j.status !== 'processing');
  if (cleaned.length !== jobs.length) {
    writeJobs(cleaned);
  }
})();

// Track page-unload so failJob can skip persisting errors caused by refresh
let _isPageUnloading = false;
if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', () => { _isPageUnloading = true; });
}

// ─── Public API (imperative, can be called outside React) ────────────────────

/**
 * Register a new processing job. Call this right before kicking off generation.
 * Returns the job object (with generated id).
 */
export function registerJob({ toolType, title, metadata = {} }) {
  const job = {
    id: `${toolType}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    toolType,
    status: 'processing',
    title: title || `Generating ${toolType}…`,
    metadata,
    startedAt: new Date().toISOString(),
    completedAt: null,
    error: null,
  };
  const jobs = pruneOldJobs(readJobs());
  jobs.unshift(job);
  writeJobs(jobs);
  window.dispatchEvent(new CustomEvent('generation:started', { detail: job }));
  return job;
}

/**
 * Mark a job as completed and remove it from the processing list.
 */
export function completeJob(jobId) {
  const jobs = readJobs().map(j =>
    j.id === jobId ? { ...j, status: 'completed', completedAt: new Date().toISOString() } : j
  );
  writeJobs(jobs);
  window.dispatchEvent(new CustomEvent('generation:completed', { detail: { jobId } }));
}

/**
 * Mark a job as failed.
 */
export function failJob(jobId, errorMessage) {
  // During page unload the SSE fetch is aborted, producing a spurious
  // network / abort error.  Don't persist that — the page-load cleanup
  // already removes 'processing' jobs so there's nothing stale left.
  if (_isPageUnloading) return;
  const jobs = readJobs().map(j =>
    j.id === jobId ? { ...j, status: 'failed', error: errorMessage || 'Generation failed', completedAt: new Date().toISOString() } : j
  );
  writeJobs(jobs);
  window.dispatchEvent(new CustomEvent('generation:failed', { detail: { jobId, error: errorMessage } }));
}

/**
 * Remove a job entirely (e.g. user dismisses a failed card).
 */
export function dismissJob(jobId) {
  const jobs = readJobs().filter(j => j.id !== jobId);
  writeJobs(jobs);
  window.dispatchEvent(new CustomEvent('generation:dismissed', { detail: { jobId } }));
}

// ─── React Hook ──────────────────────────────────────────────────────────────

/**
 * Hook that returns processing + failed jobs for a given toolType.
 * Automatically re-renders when jobs change (via custom events or cross-tab storage events).
 *
 * @param {string} toolType  – 'quiz' | 'flashcards' | 'podcast' | 'audiobook'
 * @returns {{ processingJobs: Array, failedJobs: Array, dismissJob: Function }}
 */
export function useGenerationJobs(toolType) {
  const [jobs, setJobs] = useState(() => pruneOldJobs(readJobs()));

  const refresh = useCallback(() => {
    setJobs(pruneOldJobs(readJobs()));
  }, []);

  useEffect(() => {
    // Listen to our custom events
    const events = ['generation:started', 'generation:completed', 'generation:failed', 'generation:dismissed'];
    events.forEach(e => window.addEventListener(e, refresh));
    // Also listen for cross-tab changes
    const onStorage = (e) => { if (e.key === STORAGE_KEY) refresh(); };
    window.addEventListener('storage', onStorage);
    return () => {
      events.forEach(e => window.removeEventListener(e, refresh));
      window.removeEventListener('storage', onStorage);
    };
  }, [refresh]);

  const filtered = jobs.filter(j => j.toolType === toolType);

  return {
    processingJobs: filtered.filter(j => j.status === 'processing'),
    failedJobs: filtered.filter(j => j.status === 'failed'),
    dismissJob,
  };
}
